// <select> de Categoria Financeira + botão "Gerenciar" que abre o modal de
// manutenção (issue #56). Mesmo padrão do `_categoria-select.ts`: a Categoria
// Financeira não ganha tela própria no menu.
//
// O `<select>` filtra pela direção da Conta — uma Conta a Pagar não oferece
// "Empréstimo recebido". Quem consome isto é a tela de Contas (#16).

import {
  rotuloCategoriaFinanceira,
  categoriasFinanceirasPara,
  type AplicacaoCategoria,
} from "../../domain/categorias-financeiras.ts";
import type { Store } from "../store.ts";
import { clear, el, openModal, botao } from "../ui.ts";
import { criarAcaoComando } from "./_acao.ts";
import { campo, inputTexto, select as selectCampo } from "./_cadastro.ts";
import { newId } from "../../domain/ids.ts";

export interface CategoriaFinanceiraSelect {
  element: HTMLElement;
  value(): string;
}

const ROTULO_APLICA: Record<AplicacaoCategoria, string> = {
  pagar: "Contas a Pagar",
  receber: "Contas a Receber",
  ambos: "Ambos",
};

/** `valorAtual` = id da Categoria escolhida (ou ""). */
export function categoriaFinanceiraSelect(
  store: Store,
  direcao: "pagar" | "receber",
  valorAtual: string,
): CategoriaFinanceiraSelect {
  const select = el("select", {}) as HTMLSelectElement;

  const preencher = (): void => {
    const escolhido = select.value || valorAtual;
    clear(select);
    select.append(el("option", { value: "", "data-vazio": "" }, "—"));
    const disponiveis = categoriasFinanceirasPara(store.get(), direcao);
    for (const c of disponiveis) {
      select.append(el("option", { value: c.id }, rotuloCategoriaFinanceira(c)));
    }
    select.value = disponiveis.some((c) => c.id === escolhido) ? escolhido : "";
  };
  preencher();

  const gerir = botao(
    "secundario",
    { title: "Gerenciar categorias financeiras" },
    "Gerenciar",
  );
  gerir.addEventListener("click", () =>
    abrirGerenciarCategoriasFinanceiras(store, preencher),
  );

  return {
    element: el("div", { class: "lista-select" }, select, gerir),
    value: () => select.value,
  };
}

const OPCOES_APLICA: readonly (readonly [string, string])[] = [
  ["pagar", ROTULO_APLICA.pagar],
  ["receber", ROTULO_APLICA.receber],
  ["ambos", ROTULO_APLICA.ambos],
];

/** Modal de manutenção. `aoMudar` roda depois de cada gravação. */
export function abrirGerenciarCategoriasFinanceiras(
  store: Store,
  aoMudar?: () => void,
): void {
  openModal("Gerenciar Categorias Financeiras", () => {
    const raiz = el("div", { class: "form-modal" });
    let editandoId: string | null = null;

    // O formulário (criar e editar usam o mesmo): três campos empilhados no
    // padrão do sistema — `campo()` — em vez de caixas soltas numa linha só,
    // que não cabem para três campos.
    const camposCategoria = (
      nome: string,
      sub: string,
      aplica: AplicacaoCategoria,
    ): {
      nome: HTMLInputElement;
      sub: HTMLInputElement;
      aplica: HTMLSelectElement;
      blocos: HTMLElement[];
    } => {
      const inpNome = inputTexto("Ex.: Insumos", nome);
      const inpSub = inputTexto("Opcional", sub);
      const selAplica = selectCampo(OPCOES_APLICA, aplica);
      return {
        nome: inpNome,
        sub: inpSub,
        aplica: selAplica,
        blocos: [
          campo("Nome", inpNome),
          campo("Subcategoria", inpSub),
          campo("Onde se aplica", selAplica),
        ],
      };
    };

    // O `<select>` só tem os três valores de APLICACOES_CATEGORIA; estreitar
    // aqui é total e dispensa um `as` (ADR-0013).
    const aplicaDe = (s: HTMLSelectElement): AplicacaoCategoria =>
      s.value === "pagar" ? "pagar" : s.value === "receber" ? "receber" : "ambos";

    const render = (): void => {
      clear(raiz);
      const cats = [...store.get().categoriasFinanceiras].sort(
        (a, b) =>
          a.nome.localeCompare(b.nome, "pt-BR") ||
          a.subcategoria.localeCompare(b.subcategoria, "pt-BR"),
      );
      const lista = el("ul", { class: "lista-editavel" });
      if (cats.length === 0) {
        lista.append(el("li", { class: "muted" }, "Nenhuma categoria financeira."));
      }

      for (const c of cats) {
        if (editandoId === c.id) {
          const f = camposCategoria(c.nome, c.subcategoria, c.aplicaA);
          const erro = el("p", { class: "form-erro", role: "alert" });
          const ok = botao("primario", {}, "Salvar");
          const cancelar = botao("secundario", {}, "Cancelar");
          ok.addEventListener("click", () => {
            criarAcaoComando(store, erro)(
              {
                agregado: "categoriaFinanceira",
                cmd: {
                  tipo: "editar",
                  id: c.id,
                  dados: {
                    nome: f.nome.value,
                    subcategoria: f.sub.value,
                    aplicaA: aplicaDe(f.aplica),
                  },
                },
              },
              "Categoria salva.",
              () => {
                editandoId = null;
                render();
                aoMudar?.();
              },
            );
          });
          cancelar.addEventListener("click", () => {
            editandoId = null;
            render();
          });
          lista.append(
            el(
              "li",
              { class: "cat-fin-editando" },
              ...f.blocos,
              el("div", { class: "modal-actions" }, cancelar, ok),
              erro,
            ),
          );
          continue;
        }

        const acoes = el("span", { class: "hist-acao" });
        if (c.sistema) {
          // A Categoria de sistema não oferece ação nenhuma: a trava está no
          // domínio, mas mostrar botão que só dá erro é ruído.
          acoes.append(el("span", { class: "muted" }, "Do Sistema"));
        } else {
          const editar = botao({ variante: "secundario", tamanho: "compacto" }, {}, "Editar");
          editar.addEventListener("click", () => {
            editandoId = c.id;
            render();
          });
          const alternar = botao(
            { variante: "secundario", tamanho: "compacto" },
            {},
            c.ativo ? "Inativar" : "Reativar",
          );
          alternar.addEventListener("click", () => {
            void store
              .executar(
                {
                  agregado: "categoriaFinanceira",
                  cmd: { tipo: "definirAtivo", id: c.id, ativo: !c.ativo },
                },
                c.ativo ? "Categoria inativada." : "Categoria reativada.",
              )
              .then(
                () => {
                  render();
                  aoMudar?.();
                },
                () => render(),
              );
          });
          acoes.append(editar, alternar);
        }

        lista.append(
          el(
            "li",
            c.ativo ? {} : { class: "inativo" },
            el(
              "span",
              {},
              rotuloCategoriaFinanceira(c),
              el("small", { class: "muted" }, ` · ${ROTULO_APLICA[c.aplicaA]}`),
              c.ativo ? "" : " (inativa)",
            ),
            acoes,
          ),
        );
      }

      const nova = camposCategoria("", "", "pagar");
      const incluir = botao("primario", {}, "Incluir");
      const erroNovo = el("p", { class: "form-erro", role: "alert" });
      const addUma = (): void => {
        criarAcaoComando(store, erroNovo)(
          {
            agregado: "categoriaFinanceira",
            cmd: {
              tipo: "criar",
              id: newId(),
              dados: {
                nome: nova.nome.value,
                subcategoria: nova.sub.value,
                aplicaA: aplicaDe(nova.aplica),
              },
            },
          },
          "Categoria criada.",
          () => {
            render();
            aoMudar?.();
          },
        );
      };
      incluir.addEventListener("click", addUma);
      for (const inp of [nova.nome, nova.sub]) {
        inp.addEventListener("keydown", (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            addUma();
          }
        });
      }

      raiz.append(
        lista,
        el("h3", { class: "form-modal-sub" }, "Nova Categoria"),
        ...nova.blocos,
        el("div", { class: "modal-actions" }, incluir),
        erroNovo,
      );
    };

    render();
    return raiz;
  });
}
